import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box
} from '@mui/material'
import CompareIcon from '@mui/icons-material/Compare'
import PhotoSizeSelectLargeIcon from '@mui/icons-material/PhotoSizeSelectLarge'
import PhotoFilterIcon from '@mui/icons-material/PhotoFilter'
import CropIcon from '@mui/icons-material/Crop'
import CollectionsIcon from '@mui/icons-material/Collections'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ThreeDRotationIcon from '@mui/icons-material/ThreeDRotation'
import { useAuth } from '../contexts/AuthContext'
import AppHeader from '../components/common/AppHeader'

interface PhotoTool {
  id: string
  title: string
  description: string
  icon: React.ReactElement
  path: string
  color: string
}

const photoTools: PhotoTool[] = [
  {
    id: 'compare',
    title: 'Photo Compare',
    description: 'Porównaj dwa zdjęcia z efektem odsłaniania kurtyny',
    icon: <CompareIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/compare',
    color: '#2196F3' // Blue
  },
  {
    id: 'format',
    title: 'Format Converter',
    description: 'Konwertuj formaty i proporcje zdjęć',
    icon: <PhotoFilterIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/format',
    color: '#4CAF50' // Green
  },
  {
    id: 'frame',
    title: 'Frame Tool',
    description: 'Dodaj ramkę wokół zdjęcia',
    icon: <PhotoSizeSelectLargeIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/frame',
    color: '#FF9800' // Orange
  },
  {
    id: 'crop',
    title: 'Crop Tool',
    description: 'Przytnij zdjęcie do określonych rozmiarów',
    icon: <CropIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/crop',
    color: '#9C27B0' // Purple
  },
  {
    id: 'combine',
    title: 'Combine Tool',
    description: 'Połącz wiele obrazów w jeden z podpisami',
    icon: <CollectionsIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/combine',
    color: '#E91E63' // Pink
  },
  {
    id: 'maska',
    title: 'Maska',
    description: 'Segmentacja ciała i generowanie maski (BodyPix lub SAM3)',
    icon: <AutoFixHighIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/maska',
    color: '#009688' // Teal
  },
  {
    id: 'lingerie-fix',
    title: 'Popraw modelkę w bieliźnie',
    description: 'Cover (SAM3 lub czarne prostokąty) → Strawberry Pro → Uncover',
    icon: <VisibilityOffIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/lingerie-fix',
    color: '#455A64' // Blue Grey
  },
  {
    id: 'avatar-3d',
    title: '3D Avatar',
    description: 'Konwersja zdjęcia do modelu 3D (GLB) + podgląd 3D',
    icon: <ThreeDRotationIcon sx={{ fontSize: 60 }} />,
    path: '/photo-tools/avatar-3d',
    color: '#3F51B5' // Indigo
  }
]

const PhotoToolsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, logout, isAdmin } = useAuth()

  const handleToolClick = (path: string) => {
    navigate(path)
  }

  const handleBackClick = () => {
    navigate('/modes')
  }

  return (
    <>
      <AppHeader
        title="Photo Tools"
        username={user?.username}
        showAdminButton={isAdmin}
        showBackButton
        onBackClick={handleBackClick}
        onLogout={logout}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
            Narzędzia do edycji i porównywania zdjęć
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Wybierz narzędzie, które chcesz użyć
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {photoTools.map((tool) => (
            <Grid item xs={12} sm={6} md={6} key={tool.id}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardActionArea
                  onClick={() => handleToolClick(tool.path)}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    p: 3
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 2,
                      color: tool.color
                    }}
                  >
                    {tool.icon}
                  </Box>
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 0 }}>
                    <Typography variant="h5" component="h2" gutterBottom fontWeight={600}>
                      {tool.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tool.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Wszystkie narzędzia działają lokalnie w Twojej przeglądarce lub na serwerze
          </Typography>
        </Box>
      </Container>
    </>
  )
}

export default PhotoToolsPage
